import {
  Box,
  Divider,
  IconButton,
  Popover,
  Slider,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

const SortableColumnItem = ({
  column,
  isVisible,
  onToggle,
  columnWidth,
  onWidthChange,
  isLast,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <Box
        className="flex items-center gap-2 py-2 px-3 rounded-lg"
        sx={{
          opacity: isDragging ? 0.8 : 1,
          backgroundColor: isDragging
            ? colors.info + '15'
            : 'transparent',
          border: isDragging
            ? `1px solid ${colors.info}40`
            : '1px solid transparent',
          transition: 'background-color 0.15s, border-color 0.15s',
          '&:hover': {
            backgroundColor: colors.card,
          },
        }}
      >
        <Box
          {...attributes}
          {...listeners}
          className="cursor-grab flex items-center"
          sx={{
            color: colors.grey[500],
            opacity: 0.5,
            transition: 'opacity 0.15s',
            '&:hover': { opacity: 1 },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: '18px' }} />
        </Box>

        <Switch
          checked={isVisible}
          onChange={() => onToggle(column.key)}
          size="small"
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: colors.info,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: colors.info,
            },
          }}
        />

        <Typography
          className="flex-1 truncate"
          sx={{
            fontSize: '13px',
            fontWeight: isVisible ? 500 : 400,
            opacity: isVisible ? 1 : 0.5,
            transition: 'opacity 0.15s',
          }}
        >
          {column.header || column.key}
        </Typography>

        <Box
          className="flex items-center gap-2"
          sx={{ minWidth: '110px', ml: 1 }}
        >
          <Slider
            value={columnWidth || 0}
            onChange={(e, value) => onWidthChange(column.key, value)}
            min={0}
            max={400}
            step={10}
            size="small"
            sx={{
              width: '70px',
              color: colors.info,
              '& .MuiSlider-thumb': {
                width: 12,
                height: 12,
              },
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) =>
              value === 0 ? t('Auto') : `${value}px`
            }
          />
          <Typography
            sx={{
              fontSize: '10px',
              color: colors.grey[500],
              minWidth: '32px',
              textAlign: 'right',
              fontFamily: 'monospace',
            }}
          >
            {columnWidth ? `${columnWidth}px` : t('Auto')}
          </Typography>
        </Box>
      </Box>
      {!isLast && (
        <Divider
          sx={{
            mx: 2,
            borderColor: colors.info + '15',
          }}
        />
      )}
    </Box>
  );
};

const ExoColumnVisibilityMenu = ({
  columns,
  hiddenColumns,
  columnOrder,
  columnWidths,
  onToggle,
  onReorder,
  onWidthChange,
  onReset,
  smallIcon = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const hasCustomizations =
    hiddenColumns?.length > 0 ||
    (columnOrder?.length > 0) ||
    Object.keys(columnWidths || {}).length > 0;

  // Sort columns by order
  const orderedColumns = [...columns].sort((a, b) => {
    const orderA = columnOrder?.indexOf(a.key) ?? -1;
    const orderB = columnOrder?.indexOf(b.key) ?? -1;
    if (orderA === -1 && orderB === -1) return 0;
    if (orderA === -1) return 1;
    if (orderB === -1) return -1;
    return orderA - orderB;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = orderedColumns.findIndex(
        (col) => col.key === active.id
      );
      const newIndex = orderedColumns.findIndex((col) => col.key === over.id);
      const newOrder = arrayMove(
        orderedColumns.map((col) => col.key),
        oldIndex,
        newIndex
      );
      onReorder(newOrder);
    }
  };

  const visibleCount = columns.length - (hiddenColumns?.length || 0);

  return (
    <Box>
      <Tooltip title={t('Columns')}>
        <IconButton
          onClick={handleOpen}
          size={smallIcon ? 'small' : 'medium'}
          sx={{
            color: hasCustomizations ? colors.info : 'inherit',
            position: 'relative',
          }}
        >
          <ViewColumnIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: colors.glassSolid,
              backgroundImage: 'none',
              borderRadius: '12px',
              border: `1px solid ${colors.info}25`,
              boxShadow: `0 8px 32px rgba(0, 0, 0, 0.2)`,
              minWidth: '360px',
              maxHeight: '70vh',
            },
          },
        }}
      >
        {/* Header */}
        <Box
          className="flex justify-between items-center px-4 py-3"
          sx={{
            borderBottom: `1px solid ${colors.info}20`,
          }}
        >
          <Box className="flex items-center gap-2">
            <ViewColumnIcon
              sx={{ fontSize: '18px', color: colors.info }}
            />
            <Typography fontWeight={600} sx={{ fontSize: '14px' }}>
              {t('Columns')}
            </Typography>
            <Typography
              sx={{
                fontSize: '11px',
                color: colors.grey[500],
                backgroundColor: colors.bgInput,
                px: 1,
                py: 0.25,
                borderRadius: '6px',
              }}
            >
              {visibleCount}/{columns.length}
            </Typography>
          </Box>
          {onReset && hasCustomizations && (
            <Tooltip title={t('Reset Columns')}>
              <IconButton
                size="small"
                onClick={onReset}
                sx={{
                  color: colors.grey[500],
                  '&:hover': {
                    color: colors.error,
                    backgroundColor: colors.error + '15',
                  },
                }}
              >
                <RestartAltIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Column list */}
        <Box
          className="flex flex-col py-1 overflow-y-auto"
          sx={{ maxHeight: 'calc(70vh - 60px)' }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={orderedColumns.map((col) => col.key)}
              strategy={verticalListSortingStrategy}
            >
              {orderedColumns.map((column, index) => {
                const isVisible = !hiddenColumns?.includes(column.key);
                const width = columnWidths?.[column.key] || 0;
                return (
                  <SortableColumnItem
                    key={column.key}
                    column={column}
                    isVisible={isVisible}
                    onToggle={onToggle}
                    columnWidth={width}
                    onWidthChange={onWidthChange}
                    isLast={index === orderedColumns.length - 1}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </Box>
      </Popover>
    </Box>
  );
};

export default ExoColumnVisibilityMenu;
